<template>
  <div
    class="cx-text-area"
    @mouseenter="isTooltipActive=true"
    @mouseleave="isTooltipActive=false">
    <cx-label v-if="label" :label="label"/>
    <div class="cx-text-area-content">
      <div
        class="cx-position-bottom"
        v-cx-tooltip="{
          text: tooltip,
          show: isTooltipActive && tooltip !== undefined,
          position: 'top-left',
          offsetTop: -5,
          maxWidth: 400,
        }">
        <div class="cx-text-area-container" :class="{ 'cx-text-area-container--error': v$.modelValue && !isInputValid }" :disabled="disabled || null">
          <div class="cx-text-area-wrapper">
            <textarea
              v-model="innerValue"
              :disabled="disabled"
              :placeholder="placeholder"
              :readonly="readonly"
              @blur="onBlur">
            </textarea>
          </div>
        </div>
        <div class="cx-text-area-validation-wrapper">
          <span v-if="!isInputValid && showErrors" class="cx-text-area-validation">
            {{ errorMessage }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CxLabel from '@/components/CxLabel.vue';
import { useVuelidate, ValidationArgs } from '@vuelidate/core';
import { computed, ref, reactive, watch, Ref, WritableComputedRef } from 'vue';

interface Props {
  modelValue?: string,
  validationRules?: ValidationArgs<unknown> | Ref<ValidationArgs<unknown>>
  disabled?: boolean
  placeholder?: string
  label?: string
  readonly?: boolean
  tooltip?: string,
  showErrors?: boolean,
  immediatelyShowErrors?: boolean,
  onValidated?: Function
}

const props = withDefaults(defineProps<Props>(), {
  showErrors: true,
  immediatelyShowErrors: false,
  readonly: false,
  placeholder: ''
});

const emit = defineEmits<{(e: 'update:modelValue', value: string | undefined) }>();

// State for validation
const state = reactive({
  modelValue: props.modelValue
});

const initalizeVuelidate = () => {
  return props.validationRules !== undefined ? useVuelidate(props.validationRules, state) : useVuelidate();
};
let v$ = initalizeVuelidate();

const innerValue = ref(props.modelValue ? `${props.modelValue}` : '');
const isInputValid = ref(true);
const errorMessage = ref('');
const isTooltipActive = ref(false);

const onBlur = () => {
  if (!props.immediatelyShowErrors && v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else if (!props.immediatelyShowErrors && v$.value.$errors.length === 0) {
    isInputValid.value = true;
    errorMessage.value = '';
  }
};

watch(() => (props.validationRules), () => {
  v$ = initalizeVuelidate();
  v$.value.$validate();
  if (v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else {
    isInputValid.value = true;
    errorMessage.value = '';
  }
});

watch(() => (props.modelValue), (newValue) => {
  state.modelValue = newValue;
});

watch(innerValue, (newValue) => {
  state.modelValue = newValue;
  if (props.immediatelyShowErrors && v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else if (props.immediatelyShowErrors && v$.value.$errors.length === 0) {
    isInputValid.value = true;
    errorMessage.value = '';
  }
  if (!props.readonly) {
    emit('update:modelValue', newValue);
  }
  if (props.onValidated && !props.disabled) props.onValidated(newValue);
});

</script>

<style lang="less" scoped>
@import '../common';

.cx-text-area {
  font-size: @appFontSize;
  font-family: @appFontFamily;
  color: @textColor;
  height: 100%;

  .cx-label {
    padding: 0 0 3px 12px;
    text-align: left;
  }

  .cx-text-area-content {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .cx-label {
    font-size: @appFontSize;
    font-weight: @appFontWeight;
    padding: 0 0 3px 12px;
    color: @textColorLowlight;
    text-align: start;
  }

  .cx-position-right {
    display: flex;
    align-items: center;

    input {
      flex: 1 0 auto;
    }

    .cx-text-area-validation {
      flex: 0 0 200px;
    }
  }

  .cx-position-bottom {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: start;
    height: 100%;

    .cx-text-area-validation {
      padding-top: 8px;
      flex: 0 0 auto;
    }
  }

  .cx-text-area-validation-wrapper {
    padding-top: 4px;
    height: 16px;
  }
  .cx-text-area-container {
    background-color: @inputBackgroundHover;
    display: flex;
    border-radius: @inputBorderRadius;
    caret-color: @textColor;
    color: @textColor;
    width: 100%;
    height: 100%;

    &[disabled] {
      background-color: @inputBackgroundDisabled;
    }

    &:hover {
      .cx-text-area-append-icon {
        color: @textColor;
      }
    }

    &.cx-text-area-container--error {
      background-color: @inputBackgroundError;
    }

    .cx-text-area-wrapper {
      flex: 1 1 auto;
      padding: 8px 8px 7px 12px;
    }
    textarea {
      width: 100%;
      height: 100%;
      padding-top: 1px;
      font-size: @appFontSize;
      font-weight: @appFontWeight;
      border-radius: @inputBorderRadius;
      background-color: inherit;
      color: inherit;
      outline: none;
      border: none;
      resize: none;

        &:disabled {
        caret-color: @textColorDisabled;
        color: @textColorDisabled;
      }

      &:hover {
        &:not(:disabled) {
          caret-color: @textColorHighlight;
          color: @textColorHighlight;
        }
      }

      &:focus {
        caret-color: @textColorHighlight;
        color: @textColorHighlight;
        outline: none;
        border: none;
      }
    }
  }

  .cx-text-area-validation {
    font-size: @appFontSize;
    font-weight: @appFontWeight;
    padding-left: 3px;
    color: @textColorError;
    left: 0;
  }
}
</style>